import React from 'react';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import styles from './IntegrationImportTab.module.scss';

const STATUS_LABELS = {
  imported: 'Created',
  invited: 'Invited',
  mapped: 'Linked',
  skipped: 'Skipped',
  error: 'Failed',
};

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'imported', label: 'Created' },
  { key: 'invited', label: 'Invited' },
  { key: 'mapped', label: 'Linked' },
  { key: 'skipped', label: 'Skipped' },
  { key: 'error', label: 'Failed' },
];

function fmtDate(d) {
  if (!d) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

// Pure presentational — no Redux/react-query/service calls. Rendered by both
// IntegrationImportTab.component.jsx (real, Firestore-backed) and
// IntegrationImportTab.demo.component.jsx (static fixture-backed).
export const IntegrationImportTabView = ({
  companyId,
  teamId,
  source,
  sourceName,
  loading,
  logMeta,
  counts,
  statusFilter,
  onStatusFilterChange,
  filteredEmployees,
  reimporting,
  onReimport,
}) => {
  if (!companyId) {
    return (
      <div className={styles.emptyState}>
        Select a company to view {sourceName} import data.
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.spinner} />
        Loading import data…
      </div>
    );
  }

  if (!logMeta) {
    return (
      <EmptyState
        illustration
        title="No Import Runs Yet"
        description="Run an import from the Integrations page to see import history here."
      />
    );
  }

  return (
    <div className={styles.root}>
      {/* Last run info banner */}
      <div className={styles.runBanner}>
        <div className={styles.runBannerLeft}>
          <span className={styles.runDate}>
            Last import · {fmtDate(logMeta.importedAt)}
          </span>
          <span className={styles.runBy}>
            by{' '}
            {logMeta.importedBy?.name ||
              logMeta.importedBy?.userId ||
              'Unknown'}
          </span>
        </div>
        <div className={styles.runStats}>
          {(logMeta.stats?.imported || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipGreen}`}>
              {logMeta.stats.imported} created
            </span>
          )}
          {(logMeta.stats?.invited || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipBlue}`}>
              {logMeta.stats.invited} invited
            </span>
          )}
          {(logMeta.stats?.mapped || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipGray}`}>
              {logMeta.stats.mapped} linked
            </span>
          )}
          {(logMeta.stats?.skipped || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipGray}`}>
              {logMeta.stats.skipped} skipped
            </span>
          )}
          {(logMeta.stats?.errors || 0) > 0 && (
            <span className={`${styles.chip} ${styles.chipRed}`}>
              {logMeta.stats.errors} failed
            </span>
          )}
        </div>
        <Button
          text={reimporting ? 'Re-importing…' : `Re-import from ${sourceName}`}
          variant={buttonVariants.primary}
          size={buttonSizes.sm}
          onClick={onReimport}
          disabled={reimporting || !teamId}
        />
      </div>

      {/* Status filter chips */}
      <div className={styles.filterRow}>
        {STATUS_FILTERS.map(({ key, label }) => {
          if (key !== 'all' && counts[key] === 0) return null;
          return (
            <button
              key={key}
              className={`${styles.filterChip} ${statusFilter === key ? styles.filterChipActive : ''}`}
              onClick={() => onStatusFilterChange(key)}
            >
              {label}
              <span className={styles.filterCount}>{counts[key]}</span>
            </button>
          );
        })}
      </div>

      {/* Employee list */}
      {filteredEmployees.length === 0 ? (
        <EmptyState
          illustration
          title="No Employees Match Filter"
          description="No employees match the selected status filter. Try a different filter."
        />
      ) : (
        <div className={styles.table}>
          <div className={styles.tableHeader}>
            <span>Name</span>
            <span>Email</span>
            <span>{source === 'quickbooks' ? 'QB ID' : 'SF Contact ID'}</span>
            <span>Status</span>
          </div>
          {filteredEmployees.map((emp, idx) => (
            <div key={emp.externalId ?? idx} className={styles.tableRow}>
              <span className={styles.empName}>{emp.name || '—'}</span>
              <span className={styles.empEmail}>
                {emp.email || <span className={styles.noEmail}>No email</span>}
              </span>
              <span className={styles.empId}>{emp.externalId || '—'}</span>
              <span className={styles.empStatus}>
                <span
                  className={`${styles.statusTag} ${styles[`tag_${emp.outcome}`]}`}
                >
                  {STATUS_LABELS[emp.outcome] || emp.outcome}
                </span>
                {emp.outcome === 'error' && emp.reason && (
                  <span className={styles.errorReason}>{emp.reason}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
